/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Button, Text, theme } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { ContractInlineDatePicker } from "~/components/Contract/ContractInlineEdit/ContractInlineDatePicker";
import { ContractInlineDurationSelector } from "~/components/Contract/ContractInlineEdit/ContractInlineDurationSelector";
import {
  contractEditModeInitialState,
  ContractEditModeType,
  ContractLoadingType,
} from "~/components/Contract/ContractItem";
import { Contract, DurationType, SubmitContract } from "~/models";
import {
  useShowComponent,
  checkSignedDate,
  checkStartsDate,
  checkEndsDate,
} from "~/tools";

type ContractDurationDatesProps = {
  item: Contract;
  onSubmit: (values: SubmitContract) => Promise<void>;
  isLoading: ContractLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<ContractLoadingType>>;
  isEditMode: ContractEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<ContractEditModeType>>;
  canEdit?: boolean;
};

const StyledText = styled(Text)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text02};
  margin: 0.8rem 0;
  width: 7rem;
`;

export const StyledErrorText = styled(Text)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text02};
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
function ContractDurationDates({
  item,
  onSubmit,
  isLoading,
  setIsLoading,
  isEditMode,
  setIsEditMode,
  canEdit,
}: ContractDurationDatesProps) {
  const [signedError, setSignedError] = React.useState<string | undefined>(
    undefined
  );
  const [startsError, setStartsError] = React.useState<string | undefined>(
    undefined
  );
  const [endsError, setEndsError] = React.useState<string | undefined>(
    undefined
  );
  const [durationType, setDurationType] = React.useState<string>(
    item.durationType
  );
  const [signed, setSigned] = React.useState<Dayjs | null>(
    item.signed ? dayjs(item.signed) : null
  );
  const [starts, setStarts] = React.useState<Dayjs | null>(
    item.starts ? dayjs(item.starts) : null
  );
  const [ends, setEnds] = React.useState<Dayjs | null>(
    item.ends ? dayjs(item.ends) : null
  );

  const cancelHandle = React.useCallback(() => {
    setIsEditMode(contractEditModeInitialState);
    setDurationType(item.durationType);
    setSigned(item.signed ? dayjs(item.signed) : null);
    setEnds(item.ends ? dayjs(item.ends) : null);
    setStarts(item.starts ? dayjs(item.starts) : null);
    setSignedError(undefined);
    setStartsError(undefined);
    setEndsError(undefined);
  }, [item.durationType, item.ends, item.signed, item.starts, setIsEditMode]);

  React.useEffect(() => {
    cancelHandle();
  }, [cancelHandle, item]);

  const { handleSubmit } = useForm();

  const showEditCancelBtn = useShowComponent(
    [
      item.durationType,
      item.signed && dayjs(item.signed).toString(),
      item.starts && dayjs(item.starts).toString(),
      item.ends && dayjs(item.ends).toString(),
    ],
    [
      durationType,
      signed && dayjs(signed).toString(),
      starts && dayjs(starts).toString(),
      ends && dayjs(ends).toString(),
    ]
  );

  React.useEffect(() => {
    setSignedError(checkSignedDate(durationType, signed).isValidate);
  }, [durationType, signed]);

  React.useEffect(() => {
    setStartsError(checkStartsDate(durationType, starts, signed).isValidate);
  }, [durationType, starts, signed]);

  React.useEffect(() => {
    setEndsError(checkEndsDate(durationType, starts, signed, ends).isValidate);
  }, [durationType, starts, signed, ends]);

  function validContractDates() {
    return (
      !checkSignedDate(durationType, signed).isValidate &&
      !checkStartsDate(durationType, starts, signed).isValidate &&
      !checkEndsDate(durationType, starts, signed, ends).isValidate
    );
  }

  const onEdit = async () => {
    if (!validContractDates()) return;
    setIsLoading((prevState) => ({
      ...prevState,
      durationType: true,
      starts: true,
      signed: true,
      ends: durationType === DurationType.FIXED,
    }));
    const organizeValues: SubmitContract = {
      ...item,
      durationType,
      starts: starts ? starts : null,
      signed: signed ? signed : null,
      ends: durationType === DurationType.FIXED ? ends : null,
    };

    await onSubmit(organizeValues);

    setIsLoading((prevState) => ({
      ...prevState,
      durationType: false,
      starts: false,
      signed: false,
      ends: false,
    }));
  };

  return (
    <form
      onSubmit={handleSubmit(onEdit)}
      css={{ display: "block", width: "100%" }}
    >
      <FlexContainer>
        <StyledText>Duration Type:</StyledText>
        <ContractInlineDurationSelector
          durationType={durationType}
          setDurationType={setDurationType}
          property="durationType"
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          canEdit={canEdit}
        />
      </FlexContainer>
      <FlexContainer align="center">
        <StyledText>Signed At:</StyledText>
        <ContractInlineDatePicker
          left
          date={signed}
          setDate={setSigned}
          property="signed"
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          canEdit={canEdit}
        />
      </FlexContainer>
      <StyledErrorText> {signedError} </StyledErrorText>
      <FlexContainer align="center">
        <StyledText>Starts At:</StyledText>
        <ContractInlineDatePicker
          left
          date={starts}
          setDate={setStarts}
          property="starts"
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          canEdit={canEdit}
        />
      </FlexContainer>
      <StyledErrorText> {startsError} </StyledErrorText>
      {durationType === DurationType.FIXED && (
        <>
          <FlexContainer align="center">
            <StyledText>Ends at:</StyledText>
            <ContractInlineDatePicker
              left
              date={ends}
              setDate={setEnds}
              property="ends"
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              canEdit={canEdit}
            />
          </FlexContainer>
          <StyledErrorText> {endsError} </StyledErrorText>
        </>
      )}
      {showEditCancelBtn && (
        <FlexContainer align="center" width="30rem">
          <Button m="0" type="submit" icon="save" size="md" severity="low">
            Save
          </Button>
          <Button
            type="button"
            ml=".5rem"
            icon="close"
            size="md"
            severity="low"
            onClick={cancelHandle}
          >
            Cancel
          </Button>
        </FlexContainer>
      )}
    </form>
  );
}

export { ContractDurationDates };
